import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";

const features = [
  {
    title: "Intuitive UI",
    description: "A beautiful, modern interface that makes patent form automation a breeze.",
    icon: "🖥️",
    back: "No learning curve. Just start!",
    color: "from-emerald-400 to-teal-500"
  },
  {
    title: "Dynamic Generation",
    description: "Generate, preview, and download your IP forms instantly.",
    icon: "⚡",
    back: "Powered by Docxtemplater for accuracy.",
    color: "from-amber-400 to-orange-500"
  },
  {
    title: "Secure & Private",
    description: "Your data stays on your device. No cloud, no leaks.",
    icon: "🔒",
    back: "Privacy-first, always.",
    color: "from-indigo-400 to-blue-500"
  },
  {
    title: "Fast & Reliable",
    description: "Lightning-fast document creation, every time.",
    icon: "🚀",
    back: "Save hours on paperwork.",
    color: "from-purple-400 to-pink-500"
  },
];



const Typewriter = ({ text, speed = 60, className = "" }) => {
  const [displayed, setDisplayed] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayed(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, speed]);

  // Reset when text changes
  useEffect(() => {
    setDisplayed("");
    setCurrentIndex(0);
  }, [text]);

  return (
    <span className={`font-bold ${className}`}>
      {displayed}
      <span className="animate-pulse">|</span>
    </span>
  );
};

const FeatureCard = ({ feature, index }) => {
  const [flipped, setFlipped] = useState(false);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { delay: index * 0.15 }
      });
    }
  }, [controls, inView, index]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={controls}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="relative w-64 h-64 perspective group cursor-pointer"
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <div className={`transition-transform duration-700 transform-style-preserve-3d w-full h-full ${flipped ? "rotate-y-180" : ""}`}>
        <div className={`absolute w-full h-full bg-gradient-to-br ${feature.color} rounded-2xl shadow-2xl flex flex-col items-center justify-center gap-4 p-6 backface-hidden border-2 border-white/30`}>
          <motion.div 
            animate={{ rotate: flipped ? 0 : [0, 15, -15, 0] }}
            transition={{ duration: 0.5 }}
            className="text-5xl"
          >
            {feature.icon}
          </motion.div>
          <h4 className="text-xl font-bold text-white">{feature.title}</h4>
          <p className="text-white/90 text-center">{feature.description}</p>
        </div>

        <div className={`absolute w-full h-full bg-white rounded-2xl shadow-2xl flex flex-col items-center justify-center gap-4 p-6 rotate-y-180 backface-hidden border-2 border-blue-100`}>
          <h4 className="text-xl font-bold text-gray-800">{feature.title}</h4>
          <p className="text-gray-600 text-center">{feature.back}</p>
          <div className="absolute bottom-4 right-4 text-2xl">{feature.icon}</div>
        </div>
      </div>
    </motion.div>
  );
};



const FloatingParticles = () => {
  const particles = Array(15).fill(0);
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((_, i) => (
        <motion.div
          key={i}
          initial={{
            x: Math.random() * 100,
            y: Math.random() * 100,
            opacity: 0
          }}
          animate={{
            x: [null, Math.random() * 100],
            y: [null, Math.random() * 100],
            opacity: [0, 0.3, 0]
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            repeatType: "reverse",
            delay: Math.random() * 5
          }}
          className="absolute w-1 h-1 bg-blue-400 rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
        />
      ))}
    </div>
  );
};

const LandingPage = () => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const controls = useAnimation();
  
  
  
  // Rotate feature highlights
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex flex-col relative overflow-hidden">
      {/* Animated Background Elements */}
      <FloatingParticles />
      
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-blue-300 opacity-10 rounded-full filter blur-3xl animate-float-slow z-0" />
      <div className="absolute top-1/3 -right-20 w-[500px] h-[500px] bg-purple-200 opacity-10 rounded-full filter blur-3xl animate-float-medium z-0" />
      <div className="absolute bottom-20 left-1/4 w-[400px] h-[400px] bg-teal-200 opacity-10 rounded-full filter blur-3xl animate-float-fast z-0" />

      {/* Main Content */}
      <main className="flex flex-col items-center gap-16 w-full max-w-7xl mx-auto flex-1 px-4 py-10 z-10 relative">
        {/* Hero Section */}
        <section className="flex flex-col items-center text-center mt-10 md:mt-20">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-2 px-4 py-1 bg-blue-100 rounded-full text-blue-600 font-medium text-sm inline-flex items-center"
          >
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
            Now more faster
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-5xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400 relative"
          >
            <Typewriter text="AutoIP" speed={150} />
          </motion.h1>
          
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-2xl md:text-4xl font-bold text-gray-800 mt-4 max-w-2xl"
          >
            <Typewriter text="Revolutionize Your IP Workflow" speed={60} />
          </motion.h3>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-6 text-lg md:text-xl text-blue-700 max-w-2xl leading-relaxed"
          >
            AutoIP is your intelligent patent document assistant – transforming hours of paperwork into minutes of simplicity.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/form135"
              className="bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold text-lg px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-500 transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0"
            >
              Legacy Forms (1, 3, 5, 28) →
            </Link>
            <Link
              to="/form-selector"
              className="bg-gradient-to-r from-green-600 to-emerald-400 text-white font-bold text-lg px-8 py-3 rounded-full shadow-lg hover:shadow-xl hover:from-green-700 hover:to-emerald-500 transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0"
            >
              All Forms (2, 4, 6, 7, 8, 9, 10+) →
            </Link>
          </motion.div>
        </section>

        {/* Feature Highlight */}
        <motion.section 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="w-full mt-16"
        >
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-xl p-8 md:p-12 border border-blue-100 overflow-hidden relative">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-400 rounded-full opacity-10 filter blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-400 rounded-full opacity-10 filter blur-3xl"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Featured Capability</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-8"></div>
              
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-1">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentFeature}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.5 }}
                    >
                      <h3 className="text-2xl font-bold text-blue-600 mb-2">{features[currentFeature].title}</h3>
                      <p className="text-gray-700 text-lg mb-6">{features[currentFeature].description}</p>
                      <div className="flex gap-2">
                        {features.map((_, idx) => (
                          <button 
                            key={idx}
                            onClick={() => setCurrentFeature(idx)}
                            className={`w-3 h-3 rounded-full transition-all ${currentFeature === idx ? 'bg-blue-600 w-6' : 'bg-blue-200'}`}
                          />
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
                
                <div className="flex-1 flex justify-center">
                  <div className="relative w-64 h-64">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentFeature}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                        className={`absolute inset-0 bg-gradient-to-br ${features[currentFeature].color} rounded-2xl shadow-lg flex items-center justify-center text-8xl`}
                      >
                        {features[currentFeature].icon}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Feature Cards Grid */}
        <section className="w-full text-center mt-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-gray-800 mb-4"
          >
            Powerful Features
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg text-gray-600 max-w-3xl mx-auto mb-12"
          >
            Everything you need to streamline your patent documentation process
          </motion.p>
          
          <div className="flex flex-wrap justify-center gap-8">
            {features.map((feature, idx) => (
              <FeatureCard key={idx} feature={feature} index={idx} />
            ))}
          </div>
        </section>

       
        

        {/* Final CTA */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="w-full mt-20 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Ready to Transform Your IP Workflow?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join hundreds of professionals who save hours every week with AutoIP
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/form135"
              className="bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold text-lg px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-500 transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0"
            >
              Legacy Forms →
            </Link>
            <Link
              to="/form-selector"
              className="bg-white text-blue-600 font-bold text-lg px-8 py-4 rounded-full shadow-lg hover:shadow-xl border-2 border-blue-100 transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0"
            >
              Browse All Forms →
            </Link>
          </div>
        </motion.section>
      </main>

      <style jsx global>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-15px) translateX(-15px); }
        }
        @keyframes float-fast {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-10px) translateX(5px); }
        }
        .animate-float-slow { animation: float-slow 10s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 8s ease-in-out infinite; }
        .animate-float-fast { animation: float-fast 6s ease-in-out infinite; }
        .perspective { perspective: 1200px; }
        .transform-style-preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
      `}</style>
    </div>
  );
};

export default LandingPage;